import {Component, ElementRef, Input, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren} from '@angular/core';
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {MatIcon} from "@angular/material/icon";
import {MatAutocomplete, MatAutocompleteTrigger, MatOption} from "@angular/material/autocomplete";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatSelect} from "@angular/material/select";
import {MatButton} from "@angular/material/button";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {debounceTime, distinctUntilChanged, Subject, tap} from "rxjs";
import {FormatMobilePipe} from "@core/pipes/format-mobile.pipe";
import {AuthManagementService} from "@core/service/auth-management.service";
import {CreateEditService} from '@core/http/create-edit-service';
import {CatalogsService} from "@core/http/catalogs.service";
import {HttpMesageHandlerService} from "@core/service/http-mesage-handler";
import {DefaultResponse} from "@core/models/Http/DefaultResponse";
import {HttpErrorResponse} from "@angular/common/http";
import {UserDetails} from "@core/models/UserDetails";
import {BusinessGroup} from "@core/models/BusinessGroup";
import {Branch} from "@core/models/Branch";
import {Settlement} from "@core/models/Settlement";
import {UserRole} from "@core/models/Enums/UserRole";
import Swal from "sweetalert2";
import {NgxMatIntlTelInputComponent} from 'ngx-mat-intl-tel-input';
import {BranchService} from "@core/http/table-data-services/Branch.service";
import {PatientService} from '@core/http/patient.service';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {MatTooltip} from "@angular/material/tooltip";
import { MatDialog } from '@angular/material/dialog';
import { TutorService } from '@core/service/tutor.service';
import { UserService } from '@core/http/table-data-services/User.service';
import { DoctorService } from '@core/http/table-data-services/Doctor.service';
import { TutoradoProfileDialogComponent } from '@shared/components/tutorado-profile-dialog/tutorado-profile-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import {RegionWordPipe} from "@core/pipes/region-word.pipe";
import {environment} from "@enviroment/environment";

type PiePaginaData = {
  idPiePagina: number | null;
  detalle: string;
  isDefault: boolean;
};

type FormatoRecetaItem = {
  idFormatoReceta: number;
  formato: string;
  logo?: string;
  logoSuperior?: string;
  logoInferior?: string;
  imagenVistaPrevia: string;
};

type FormatoHipItem = {
  idFormatoHip: number;
  formato: string;
  logo?: string;
  logoSuperior?: string;
  logoInferior?: string;
  imagenVistaPrevia: string;
};

export const nipPacienteValidator = (length: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? '').toString().trim();
    if (!raw) return { required: true };

    // 1) exact length + digits only
    if (raw.length !== length || !/^\d+$/.test(raw)) {
      return { nipFormat: true };
    }

    // 2) no repeated digits
    const distinct = new Set(raw.split(''));
    if (distinct.size !== raw.length) {
      return { nipRepeated: true };
    }

    // 3) no ascending/descending sequences of 3
    for (let i = 0; i < raw.length - 2; i++) {
      const a = raw.charCodeAt(i) - 48;
      const b = raw.charCodeAt(i + 1) - 48;
      const c = raw.charCodeAt(i + 2) - 48;

      const asc = (b === a + 1 && c === b + 1);
      const desc = (b === a - 1 && c === b - 1);

      if (asc || desc) return { nipSequence: true };
    }

    return null;
  };
};

export const pin6DigitsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const raw = (control.value ?? '').toString().trim();
  if (!raw) return { required: true };
  if (!/^\d{6}$/.test(raw)) return { pinFormat: true };
  return null;
};

export const matchPinsValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const g: any = group;
  const newPin = (g.get?.('newPin')?.value ?? '').toString().trim();
  const confirm = (g.get?.('confirmNewPin')?.value ?? '').toString().trim();

  // ✅ SOLO VALIDAR CUANDO EL CONFIRM YA TENGA 6 DÍGITOS
  if (confirm.length < 6) return null;

  return newPin === confirm ? null : { pinMismatch: true };
};

@Component({
  selector: 'app-profile-user',
  standalone: true,
  imports: [
    FormatMobilePipe,
    MatIcon,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatFormField,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    NgForOf,
    ReactiveFormsModule,
    MatButton,
    NgIf,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    NgxMatIntlTelInputComponent,
    MatTooltip,
    MatDialogModule,
    DatePipe,
    RegionWordPipe,
  ],
  templateUrl: './profile-user.component.html',
  styleUrl: './profile-user.component.scss'
})
export class ProfileUserComponent implements OnInit {

  @Input() idGemp!: string;

  userImage: string | null = null;
  isAdmin: boolean;
  grupoEmpresarial: string | undefined;

  user: UserDetails;
  blankObject = {} as UserDetails;


  businessGroups: BusinessGroup[] = [];
  branches: Branch[] = [];

  // CP -> Colonia
  settlements: Settlement[] = [];
  settlementSearch = '';

  userForm: UntypedFormGroup;
  piePaginaForm: UntypedFormGroup;
  roles: any[];
  piePaginaId: number | null = null;
  doctorId: string | null = null;
  loadingPiePagina = false;
  savingPiePagina = false;
  deletingPiePagina = false;
  formatosReceta: FormatoRecetaItem[] = [];
  formatoRecetaActivoId: number | null = null;
  formatoRecetaSeleccionadoId: number | null = null;
  formatoRecetaPreviewActual: FormatoRecetaItem | null = null;
  loadingFormatosReceta = false;
  savingFormatoReceta = false;
  formatosHip: FormatoHipItem[] = [];
  formatoHipActivoId: number | null = null;
  formatoHipSeleccionadoId: number | null = null;
  formatoHipPreviewActual: FormatoHipItem | null = null;
  loadingFormatosHip = false;
  savingFormatoHip = false;

  preferredCountries = ['mx', 'us'];
  keyPress$ = new Subject();

  cambiarContrasena = false;
  loadingUser = false;

  protected readonly UserRole = UserRole;

  // =========================
  // PIN (SOLO PACIENTE)
  // =========================
  idPaciente: string | null = null;

  pinForm: UntypedFormGroup;

  pinDigits = Array.from({ length: 6 });

// 3 OTPs: PIN ACTUAL, PIN NUEVO, CONFIRMAR PIN NUEVO
  oldPinOtp: string[] = ['', '', '', '', '', ''];
  newPinOtp: string[] = ['', '', '', '', '', ''];
  confirmNewPinOtp: string[] = ['', '', '', '', '', ''];

  pinExists = false;
  loadingPin = false;
  oldPinValidated = false;
  validatingOldPin = false;

  // =========================
  // ✅ TUTORADOS (PERSONAS A CARGO)
  // =========================
  tutorados: any[] = [];
  tutoradoImages: Record<string, string> = {};
  isTutor = false;

  // cache simple para imágenes
  private readonly imageCache = new Map<string, string>();

// 3 grupos de inputs (en HTML usa: #oldPinDigit, #newPinDigit, #confirmNewPinDigit)
  @ViewChildren('oldPinDigit') oldPinDigitInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('newPinDigit') newPinDigitInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('confirmNewPinDigit') confirmNewPinDigitInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChild('formatoRecetaPreviewDialog') formatoRecetaPreviewDialog!: TemplateRef<any>;
  @ViewChild('formatoHipPreviewDialog') formatoHipPreviewDialog!: TemplateRef<any>;

  constructor(
    private authManagement: AuthManagementService,
    private catalogsService: CatalogsService,
    private fb: UntypedFormBuilder,
    private httpMsgHandler: HttpMesageHandlerService,
    private userService: UserService,
    private createEditService: CreateEditService,
    private branchService: BranchService,
    private patientService: PatientService,
    private tutorService: TutorService,
    private dialog: MatDialog,
    private doctorService: DoctorService,
  ) {
    this.userImage = 'assets/images/user/imgUSer.png';
    this.user = new UserDetails(this.blankObject);

    this.isAdmin = this.authManagement.isUserAdmin();
    this.userForm = this.createUserForm();
    this.piePaginaForm = this.createPiePaginaForm();

    this.pinForm = this.createPinForm();

    this.roles = Object.entries(UserRole)
      .filter(([name]) => name !== 'All' && name !== 'Medico' && name !== 'Paciente')
      .map(([name, value]) => ({ name: this.formatRoleName(name), value }));
  }

  ngOnInit(): void {
    this.getUserdetails();

    this.keyPress$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => {
          const term = this.userForm.get('settlementSearch')?.value || '';
          if (typeof term === 'string' && term.trim().length >= 5) {
            this.searchSettlements(term.trim());
          } else {
            this.settlements = [];
          }
        })
      )
      .subscribe();
  }

  get showBusinessGroupReadonly(): boolean {
    const isSpain = String(environment.region ?? '').toUpperCase() === 'ES';
    const tipoUsuario = String(this.user?.tipoUsuario ?? '').toUpperCase();

    if (!isSpain) {
      return true;
    }

    return tipoUsuario !== 'PARTICULAR' && tipoUsuario !== 'PACIENTE';
  }

  formatRoleName(name: string): string {
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  private createPinForm(): UntypedFormGroup {
    return this.fb.group(
      {
        oldPin: [{ value: '', disabled: true }],
        newPin: ['', [nipPacienteValidator(6)]],
        confirmNewPin: ['', [pin6DigitsValidator]],
      },
      { validators: [matchPinsValidator] }
    );
  }

  createUserForm(): UntypedFormGroup {
    return this.fb.group({
      idTipoUsuario: [this.user.idTipoUsuario],
      nombres: [this.user.nombres],
      primerApellido: [this.user.primerApellido],
      status: [this.user.status || 'Activo'],
      usr: [this.user.usr],
      idUsuario: [this.user.idUsuario],
      idGEMP: [this.user.idGEMP],
      idSucursal: [this.user.idSucursal],
      segundoApellido: [this.user.segundoApellido],
      idAsentamiento: [this.user.idAsentamiento],
      domicilio: [this.user.domicilio],
      movil: [this.user.movil],
      email: [this.user.email],
      password: [''],
      settlementSearch: [this.settlementSearch],
    });
  }

  createPiePaginaForm(): UntypedFormGroup {
    return this.fb.group({
      detalle: [''],
    });
  }

  // =========================
  // PIN FORM
  // =========================
  private focusOtpIndex(type: 'old' | 'new' | 'confirm', i: number) {
    const list =
      type === 'old' ? (this.oldPinDigitInputs?.toArray?.() ?? []) :
        type === 'new' ? (this.newPinDigitInputs?.toArray?.() ?? []) :
          (this.confirmNewPinDigitInputs?.toArray?.() ?? []);

    const el = list[i]?.nativeElement;
    if (el) {
      el.focus();
      el.select();
    }
  }

  private getOtpArray(type: 'old' | 'new' | 'confirm'): string[] {
    if (type === 'old') return this.oldPinOtp;
    if (type === 'new') return this.newPinOtp;
    return this.confirmNewPinOtp;
  }

  private getOtpValue(type: 'old' | 'new' | 'confirm'): string {
    return this.getOtpArray(type).join('');
  }

  isOtpComplete(type: 'old' | 'new' | 'confirm'): boolean {
    return /^\d{6}$/.test(this.getOtpValue(type));
  }

  private setOtpArray(type: 'old' | 'new' | 'confirm', value: string[]) {
    if (type === 'old') this.oldPinOtp = value;
    else if (type === 'new') this.newPinOtp = value;
    else this.confirmNewPinOtp = value;
  }

  private syncPinControl(type: 'old' | 'new' | 'confirm', markTouched = false) {
    const v = this.getOtpValue(type);
    const ctrlName = type === 'old' ? 'oldPin' : (type === 'new' ? 'newPin' : 'confirmNewPin');

    if (type === 'old') {
      this.validatingOldPin = false;
      this.oldPinValidated = false;
    }

    this.pinForm.patchValue({ [ctrlName]: v }, { emitEvent: false });

    const ctrl = this.pinForm.get(ctrlName);
    if (markTouched) ctrl?.markAsTouched();
    ctrl?.updateValueAndValidity({ emitEvent: false });

    // para que corra matchPinsValidator
    this.pinForm.updateValueAndValidity({ emitEvent: false });
  }

  private resetOtp(type: 'old' | 'new' | 'confirm') {
    this.setOtpArray(type, ['', '', '', '', '', '']);
    this.syncPinControl(type, false);
  }

  onOtpInput(event: Event, index: number, type: 'old' | 'new' | 'confirm') {
    const input = event.target as HTMLInputElement;
    let v = (input.value ?? '').replace(/\D/g, '');
    if (v.length > 1) v = v.slice(-1);

    const arr = [...this.getOtpArray(type)];
    arr[index] = v;
    this.setOtpArray(type, arr);

    this.syncPinControl(type, false);

    if (v && index < 5) this.focusOtpIndex(type, index + 1);
    if (!arr.includes('')) this.syncPinControl(type, true);
  }

  onOtpKeyDown(event: KeyboardEvent, index: number, type: 'old' | 'new' | 'confirm') {
    const key = event.key;

    const allowed =
      /^[0-9]$/.test(key) ||
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'Tab';

    if (!allowed) {
      event.preventDefault();
      return;
    }

    if (key === 'ArrowLeft') {
      event.preventDefault();
      this.focusOtpIndex(type, Math.max(0, index - 1));
      return;
    }

    if (key === 'ArrowRight') {
      event.preventDefault();
      this.focusOtpIndex(type, Math.min(5, index + 1));
      return;
    }

    if (key === 'Backspace') {
      const arr = [...this.getOtpArray(type)];
      const current = arr[index];

      if (!current && index > 0) {
        event.preventDefault();
        arr[index - 1] = '';
        this.setOtpArray(type, arr);
        this.syncPinControl(type, false);
        this.focusOtpIndex(type, index - 1);
      } else {
        setTimeout(() => {
          const list =
            type === 'old' ? (this.oldPinDigitInputs?.toArray?.() ?? []) :
              type === 'new' ? (this.newPinDigitInputs?.toArray?.() ?? []) :
                (this.confirmNewPinDigitInputs?.toArray?.() ?? []);

          const el = list[index]?.nativeElement;
          const raw = (el?.value ?? '').replace(/\D/g, '');
          arr[index] = raw ? raw.slice(-1) : '';
          this.setOtpArray(type, arr);
          this.syncPinControl(type, false);
        }, 0);
      }
      return;
    }

    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      const arr = [...this.getOtpArray(type)];
      arr[index] = key;
      this.setOtpArray(type, arr);
      this.syncPinControl(type, false);

      if (index < 5) this.focusOtpIndex(type, index + 1);
      else this.syncPinControl(type, true);
    }
  }

  onOtpPaste(event: ClipboardEvent, type: 'old' | 'new' | 'confirm') {
    event.preventDefault();
    const text = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;

    const arr = ['', '', '', '', '', ''];
    for (let i = 0; i < 6; i++) arr[i] = text[i] ?? '';

    this.setOtpArray(type, arr);
    this.syncPinControl(type, true);

    const nextEmpty = arr.findIndex(x => !x);
    setTimeout(() => this.focusOtpIndex(type, nextEmpty === -1 ? 5 : nextEmpty), 0);
  }

  // =========================
  // LOAD PERFIL
  // =========================
  getUserdetails(): void {
    if (this.loadingUser) return;
    this.loadingUser = true;

    const safe = this.authManagement.userData();
    const usr = safe?.Usr;
    const idUsuario = safe?.IdUsuario;
    const tipoUsuario = safe?.TipoUsuario;

    if (!usr && !idUsuario) {
      this.loadingUser = false;
      return;
    }

    this.userImage = safe?.Imagen
      ? ('data:image/png;base64,' + safe.Imagen)
      : 'assets/images/user/imgUSer.png';

    const isPaciente = tipoUsuario === 'Paciente' || safe?.IdTipoUsuario === UserRole.Paciente;

    if (isPaciente) {
      if (!idUsuario) {
        this.loadingUser = false;
        return;
      }
      this.loadPacienteByIdUsuario(idUsuario);
    } else {
      if (!usr) {
        this.loadingUser = false;
        return;
      }
      this.loadUsuarioByUsr(usr);
    }
  }

  private loadUsuarioByUsr(usr: string): void {
    this.createEditService.getUserByUsr(usr).subscribe({
      next: (res: DefaultResponse<UserDetails | UserDetails[]>) => {
        const raw: any = (res as any)?.data;
        const u: any = Array.isArray(raw) ? raw[0] : raw;

        if (!u) {
          this.loadingUser = false;
          return;
        }

        this.user = {
          ...this.user,
          idUsuario: u.idUsuario,
          usr: u.usr,
          idTipoUsuario: u.idTipoUsuario,
          tipoUsuario: u.tipoUsuario,
          idGEMP: u.idGEMP,
          empresa: u.empresa,
          idSucursal: u.idSucursal,
          nombreSucursal: u.nombreSucursal,
          nombres: u.nombres,
          primerApellido: u.primerApellido,
          segundoApellido: u.segundoApellido,
          idAsentamiento: u.idAsentamiento,
          nombreAsentamiento: u.nombreAsentamiento,
          codigoPostal: u.codigoPostal,
          municipio: u.municipio,
          domicilio: u.domicilio,
          movil: u.movil,
          email: this.removeAccents(u.email),
          firma: u.firma,
          imagen: u.imagen,
          status: u.status,
        } as any;

        this.idPaciente = null; // no aplica
        this.afterUserLoaded();
      },
      error: (err: HttpErrorResponse) => {
        this.loadingUser = false;
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  private loadPacienteByIdUsuario(idUsuario: string): void {
    this.createEditService.getPacienteByUsuarioId(idUsuario).subscribe({
      next: (res: any) => {
        const raw: any = res?.data;
        const p: any = Array.isArray(raw) ? raw[0] : raw;

        if (!p) {
          this.loadingUser = false;
          return;
        }

        this.idPaciente = p?.idPaciente ?? null;

        // =========================
        // 🔐 PIN ACTIVO DESDE BACKEND
        // =========================
        this.pinExists = !!p?.pinActivo;
        this.oldPinValidated = !this.pinExists;

        const oldCtrl = this.pinForm.get('oldPin');

        if (this.pinExists) {
          oldCtrl?.enable({ emitEvent: false });
          oldCtrl?.setValidators([pin6DigitsValidator]);
        } else {
          oldCtrl?.clearValidators();
          oldCtrl?.setValue('', { emitEvent: false });
          oldCtrl?.disable({ emitEvent: false });
          this.resetOtp('old');
        }

        oldCtrl?.updateValueAndValidity({ emitEvent: false });

        this.resetOtp('new');
        this.resetOtp('confirm');

        setTimeout(() => {
          if (this.pinExists) this.focusOtpIndex('old', 0);
          else this.focusOtpIndex('new', 0);
        }, 0);

        // =========================
        // DATOS DEL USUARIO
        // =========================
        this.user = {
          ...this.user,
          idUsuario: p.idUsuario ?? idUsuario,
          usr: p.usr ?? this.user.usr,
          idTipoUsuario: p.idTipoUsuario ?? UserRole.Paciente,
          tipoUsuario: 'Paciente',
          idGEMP: p.idGEMP ?? this.user.idGEMP,
          empresa: p.grupoEmpresarial ?? p.empresa ?? this.user.empresa,
          idSucursal: p.idSucursal ?? this.user.idSucursal,
          nombreSucursal: p.sucursal ?? p.nombreSucursal ?? this.user.nombreSucursal,
          nombres: p.nombres ?? this.user.nombres,
          primerApellido: p.primerApellido ?? this.user.primerApellido,
          segundoApellido: p.segundoApellido ?? this.user.segundoApellido,
          domicilio: p.domicilio ?? this.user.domicilio,
          movil: p.movil ?? this.user.movil,
          email: this.removeAccents(p.email ?? this.user.email),
          idAsentamiento: p.idAsentamiento ?? this.user.idAsentamiento,
          nombreAsentamiento: p.asentamiento ?? p.nombreAsentamiento ?? this.user.nombreAsentamiento,
          codigoPostal: p.codigoPostal ?? this.user.codigoPostal,
          municipio: p.municipio ?? this.user.municipio,
          imagen: p.imagen ?? this.user.imagen,
          status: p.status ?? this.user.status,
        } as any;

        this.afterUserLoaded();
      },
      error: (err: HttpErrorResponse) => {
        this.loadingUser = false;
        this.httpMsgHandler.handleMsg(err);
      }
    });
  }

  private afterUserLoaded(): void {
    if (this.user.idSucursal && !this.user.nombreSucursal) {
      this.branchService.getByIdSucursal(this.user.idSucursal).subscribe({
        next: (res) => {
          if (res?.data) {
            (this.user as any).nombreSucursal = (res as any).data?.nombre ?? this.user.nombreSucursal;
          }
        },
        error: () => { /* no crítico */ }
      });
    }

    this.userImage = this.user.imagen
      ? 'data:image/png;base64,' + this.user.imagen
      : (this.userImage || 'assets/images/user/imgUSer.png');

    this.settlementSearch = this.user.nombreAsentamiento || this.user.codigoPostal || '';
    this.userForm = this.createUserForm();

    if (this.isAdmin) {
      this.getBusinessGroups();
      this.getBranchesByGemp(this.user.idGEMP);
    } else {
      this.businessGroups = [];
      this.branches = [];
    }

    this.loadingUser = false;
    this.loadPiePaginaSection();

    this.loadTutoradosIfAny();
  }

  // ✅ SOLO ADMIN
  getBusinessGroups(): void {
    if (!this.isAdmin) return;

    this.catalogsService.getBusinessGroups().subscribe({
      next: (res: DefaultResponse<BusinessGroup[]>) => {
        this.businessGroups = [...res.data];
        this.grupoEmpresarial = this.businessGroups.find(v => v.idGEMP === this.idGemp)?.nombre;
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  // ✅ SOLO ADMIN
  getBranchesByGemp(idGEMP = ''): void {
    if (!this.isAdmin) return;

    if (!idGEMP) idGEMP = this.userForm.get('idGEMP')?.value;
    if (!idGEMP) return;

    this.catalogsService.getBranchesByIdGEMP(idGEMP).subscribe({
      next: (res: DefaultResponse<Branch[]>) => this.branches = [...res.data],
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  // ====== CP -> Colonia ======
  searchSettlements(term: string): void {
    this.catalogsService.getSettlementByPostalCode(term).subscribe({
      next: (res: DefaultResponse<Settlement[]>) => {
        this.settlements = [...res.data];

        const current = this.userForm.get('settlementSearch')?.value;
        const match = this.settlements.find(s => s.asentamiento === current);
        if (match) this.setSelectedSettlement(match);
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err)
    });
  }

  onSettlementBlur(): void {
    const value = this.userForm.get('settlementSearch')?.value;
    if (!value || (typeof value !== 'object' && !/^\d{5}$/.test(String(value)))) {
      this.settlements = [];
      this.userForm.patchValue({ settlementSearch: '' });
      this.user.idAsentamiento = undefined;
    }
  }

  displayFn(settlement: Settlement): string {
    return settlement && settlement.asentamiento ? settlement.asentamiento : '';
  }

  searchSettlementsByKeyPress(event: Event): void {
    this.keyPress$.next((event as KeyboardEvent).key + Date.now());
  }

  setSelectedSettlement(value: Settlement): void {
    this.user.idAsentamiento = value ? value.idAsentamiento : undefined;
    this.userForm.patchValue({
      idAsentamiento: this.user.idAsentamiento,
      settlementSearch: value
    });
  }

  toggleCambiarContrasena(): void {
    this.cambiarContrasena = !this.cambiarContrasena;
    if (!this.cambiarContrasena) {
      this.userForm.patchValue({ password: '' });
    }
  }

  removeAccents(v: string): string {
    return v ? v.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : v;
  }

  public confirmAdd(): void {
    const payload: any = this.userForm.getRawValue();

    if (payload.movil && typeof payload.movil === 'object' && payload.movil?.internationalNumber) {
      payload.movil = payload.movil.internationalNumber;
    }

    if (payload.settlementSearch && typeof payload.settlementSearch === 'object') {
      payload.idAsentamiento = payload.settlementSearch.idAsentamiento;
    }
    delete payload.settlementSearch;

    if (!this.cambiarContrasena) {
      delete payload.password;
    }

    this.userService.createUpdateUser(payload).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: 'ACTUALIZADO CORRECTAMENTE.',
          text: 'SE CERRARA LA SESION PARA APLICAR LOS CAMBIOS.',
          confirmButtonText: 'ACEPTAR'
        }).then(() => {
          this.authManagement.logout$().subscribe({});
        });
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
    });
  }

  savePiePagina(): void {
    if (!this.isDoctorUser()) return;

    const detalle = (this.piePaginaForm.get('detalle')?.value ?? '').toString().trim();
    if (!detalle) {
      Swal.fire({
        icon: 'warning',
        title: 'PIE DE PAGINA REQUERIDO',
        text: 'ESCRIBE EL TEXTO QUE DESEAS MOSTRAR EN LA RECETA.',
        confirmButtonText: 'ACEPTAR'
      }).then();
      return;
    }

    this.savingPiePagina = true;

    this.doctorService.upsertPiePagina({ detalle }).subscribe({
      next: (res) => {
        this.httpMsgHandler.handleMsg(res);
        this.loadPiePagina();
      },
      error: (err: HttpErrorResponse) => {
        this.savingPiePagina = false;
        this.httpMsgHandler.handleMsg(err);
      },
      complete: () => {
        this.savingPiePagina = false;
      }
    });
  }

  deletePiePagina(): void {
    if (!this.canDeletePiePagina || this.deletingPiePagina) return;

    Swal.fire({
      icon: 'warning',
      title: 'ELIMINAR PIE DE PAGINA',
      text: 'ESTA ACCION QUITARA EL PIE DE PAGINA DE TUS RECETAS.',
      showCancelButton: true,
      confirmButtonText: 'ELIMINAR',
      cancelButtonText: 'CANCELAR'
    }).then((result) => {
      if (!result.isConfirmed || !this.piePaginaId) return;

      this.deletingPiePagina = true;

      this.doctorService.deletePiePagina(this.piePaginaId).subscribe({
        next: (res) => {
          this.httpMsgHandler.handleMsg(res);
          this.loadPiePagina();
        },
        error: (err: HttpErrorResponse) => {
          this.deletingPiePagina = false;
          this.httpMsgHandler.handleMsg(err);
        },
        complete: () => {
          this.deletingPiePagina = false;
        }
      });
    });
  }

  private isDoctorUser(): boolean {
    const tipo = String(this.user?.tipoUsuario ?? '').trim().toUpperCase();
    return tipo === String(UserRole.Medico).toUpperCase()
      || tipo === String(UserRole.Particular).toUpperCase();
  }

  showPiePaginaSection(): boolean {
    return this.isDoctorUser();
  }

  showFormatoRecetaSection(): boolean {
    return this.isDoctorUser();
  }

  showHipSection(): boolean {
    return this.isDoctorUser() && environment.showHipFeatures;
  }

  get piePaginaPrimaryActionLabel(): string {
    return this.piePaginaId ? 'ACTUALIZAR PIE DE PAGINA' : 'GUARDAR PIE DE PAGINA';
  }

  get canDeletePiePagina(): boolean {
    return this.piePaginaId !== null && this.piePaginaId !== 1;
  }

  get canSaveFormatoReceta(): boolean {
    return this.formatoRecetaSeleccionadoId !== null
      && this.formatoRecetaSeleccionadoId !== this.formatoRecetaActivoId;
  }

  get canSaveFormatoHip(): boolean {
    return this.formatoHipSeleccionadoId !== null
      && this.formatoHipSeleccionadoId !== this.formatoHipActivoId;
  }

  private loadPiePaginaSection(): void {
    if (!this.isDoctorUser() || !this.user?.idUsuario) {
      this.clearPiePaginaForm();
      this.clearFormatoRecetaState();
      this.clearFormatoHipState();
      return;
    }

    this.doctorService.getDoctorByIdUser(String(this.user.idUsuario)).subscribe({
      next: (res: DefaultResponse<any>) => {
        this.doctorId = res?.data?.idMedico ? String(res.data.idMedico) : null;

        if (!this.doctorId) {
          this.clearPiePaginaForm();
          this.clearFormatoRecetaState();
          this.clearFormatoHipState();
          return;
        }

        this.loadPiePagina();
        this.loadFormatosReceta();
        this.loadFormatoRecetaActivo();
        if (environment.showHipFeatures) {
          this.loadFormatosHip();
          this.loadFormatoHipActivo();
        } else {
          this.clearFormatoHipState();
        }
      },
      error: () => {
        this.doctorId = null;
        this.clearPiePaginaForm();
        this.clearFormatoRecetaState();
        this.clearFormatoHipState();
      }
    });
  }

  private loadPiePagina(): void {
    if (!this.doctorId) {
      this.clearPiePaginaForm();
      return;
    }

    this.loadingPiePagina = true;

    this.doctorService.getPiePagina(undefined, this.doctorId).subscribe({
      next: (res: DefaultResponse<any>) => {
        const raw = res?.data;
        const pie = (Array.isArray(raw) ? raw[0] : raw) ?? null;
        const parsed = this.normalizePiePagina(pie);

        this.piePaginaId = parsed.idPiePagina;
        this.piePaginaForm.patchValue({ detalle: parsed.detalle }, { emitEvent: false });
      },
      error: () => {
        this.clearPiePaginaForm();
      },
      complete: () => {
        this.loadingPiePagina = false;
      }
    });
  }

  private normalizePiePagina(raw: any): PiePaginaData {
    if (!raw) {
      return { idPiePagina: null, detalle: '', isDefault: false };
    }

    const isDefault = this.toBoolean(raw.isDefault ?? raw.isDafault ?? raw.esDefault);

    if (isDefault) {
      return { idPiePagina: null, detalle: '', isDefault: true };
    }

    return {
      idPiePagina: raw.idPiePagina ?? raw.id ?? null,
      detalle: (raw.detalle ?? '').toString(),
      isDefault: false,
    };
  }

  private toBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return value.trim().toLowerCase() === 'true' || value.trim() === '1';
    return false;
  }

  private clearPiePaginaForm(): void {
    this.piePaginaId = null;
    this.piePaginaForm.patchValue({ detalle: '' }, { emitEvent: false });
    this.loadingPiePagina = false;
  }

  selectFormatoReceta(idFormatoReceta: number): void {
    this.formatoRecetaSeleccionadoId = idFormatoReceta;
  }

  saveFormatoReceta(): void {
    if (!this.doctorId || this.formatoRecetaSeleccionadoId === null || !this.canSaveFormatoReceta) return;

    this.savingFormatoReceta = true;

    this.doctorService.updateFormatoReceta({
      idMedico: this.doctorId,
      idFormatoReceta: this.formatoRecetaSeleccionadoId
    }).subscribe({
      next: (res) => {
        this.httpMsgHandler.handleMsg(res);
        this.formatoRecetaActivoId = this.formatoRecetaSeleccionadoId;
      },
      error: (err: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(err);
      },
      complete: () => {
        this.savingFormatoReceta = false;
      }
    });
  }

  openFormatoRecetaPreview(formato: FormatoRecetaItem, event?: Event): void {
    event?.stopPropagation();

    if (!formato?.imagenVistaPrevia || !this.formatoRecetaPreviewDialog) return;

    this.formatoRecetaPreviewActual = formato;

    this.dialog.open(this.formatoRecetaPreviewDialog, {
      width: '960px',
      maxWidth: '96vw',
      autoFocus: false,
      panelClass: 'formato-receta-preview-dialog'
    });
  }

  closeFormatoRecetaPreview(): void {
    this.dialog.closeAll();
    this.formatoRecetaPreviewActual = null;
  }

  private loadFormatosReceta(): void {
    this.loadingFormatosReceta = true;

    this.doctorService.getFormatosReceta().subscribe({
      next: (res: DefaultResponse<any[]>) => {
        const list = Array.isArray(res?.data) ? res.data : [];

        this.formatosReceta = list.map((row: any) => {
          const idFormatoReceta = Number(row?.idFormatoReceta ?? row?.id ?? 0);
          const formato = (row?.formato ?? '').toString();

          return {
            idFormatoReceta,
            formato,
            logo: row?.logo ?? '',
            logoSuperior: row?.logoSuperior ?? '',
            logoInferior: row?.logoInferior ?? '',
            imagenVistaPrevia: this.normalizeFormatoPreviewImage(row?.imagenVistaPreviaBase64),
          };
        }).filter((row: FormatoRecetaItem) => row.idFormatoReceta > 0);
      },
      error: (err: HttpErrorResponse) => {
        this.formatosReceta = [];
        this.httpMsgHandler.handleMsg(err);
      },
      complete: () => {
        this.loadingFormatosReceta = false;
      }
    });
  }

  private loadFormatoRecetaActivo(): void {
    if (!this.doctorId) {
      this.clearFormatoRecetaState();
      return;
    }

    this.doctorService.getFormatoRecetaByMedico(this.doctorId).subscribe({
      next: (res: DefaultResponse<any>) => {
        const idFormatoReceta = Number(res?.data?.idFormatoReceta ?? 1);
        this.formatoRecetaActivoId = idFormatoReceta;
        this.formatoRecetaSeleccionadoId = idFormatoReceta;
      },
      error: () => {
        this.formatoRecetaActivoId = 1;
        this.formatoRecetaSeleccionadoId = 1;
      }
    });
  }

  private clearFormatoRecetaState(): void {
    this.formatosReceta = [];
    this.formatoRecetaActivoId = null;
    this.formatoRecetaSeleccionadoId = null;
    this.formatoRecetaPreviewActual = null;
    this.loadingFormatosReceta = false;
    this.savingFormatoReceta = false;
  }

  private normalizeFormatoPreviewImage(value: any): string {
    const raw = (value ?? '').toString().trim();
    if (!raw) return '';
    if (raw.startsWith('data:image/')) return raw;
    return `data:image/png;base64,${raw}`;
  }

  selectFormatoHip(idFormatoHip: number): void {
    this.formatoHipSeleccionadoId = idFormatoHip;
  }

  saveFormatoHip(): void {
    if (!this.doctorId || this.formatoHipSeleccionadoId === null || !this.canSaveFormatoHip) return;

    this.savingFormatoHip = true;

    this.doctorService.updateFormatoHip({
      idMedico: this.doctorId,
      idFormatoHip: this.formatoHipSeleccionadoId
    }).subscribe({
      next: (res) => {
        this.httpMsgHandler.handleMsg(res);
        this.formatoHipActivoId = this.formatoHipSeleccionadoId;
      },
      error: (err: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(err);
      },
      complete: () => {
        this.savingFormatoHip = false;
      }
    });
  }

  openFormatoHipPreview(formato: FormatoHipItem, event?: Event): void {
    event?.stopPropagation();

    if (!formato?.imagenVistaPrevia || !this.formatoHipPreviewDialog) return;

    this.formatoHipPreviewActual = formato;

    this.dialog.open(this.formatoHipPreviewDialog, {
      width: '960px',
      maxWidth: '96vw',
      autoFocus: false,
      panelClass: 'formato-receta-preview-dialog'
    });
  }

  closeFormatoHipPreview(): void {
    this.dialog.closeAll();
    this.formatoHipPreviewActual = null;
  }

  private loadFormatosHip(): void {
    this.loadingFormatosHip = true;

    this.doctorService.getFormatosHip().subscribe({
      next: (res: DefaultResponse<any[]>) => {
        const list = Array.isArray(res?.data) ? res.data : [];

        this.formatosHip = list.map((row: any) => {
          const idFormatoHip = Number(row?.idFormatoHip ?? row?.id ?? 0);
          const formato = (row?.formato ?? '').toString();

          return {
            idFormatoHip,
            formato,
            logo: row?.logo ?? '',
            logoSuperior: row?.logoSuperior ?? '',
            logoInferior: row?.logoInferior ?? '',
            imagenVistaPrevia: this.normalizeFormatoPreviewImage(row?.imagenVistaPreviaBase64),
          };
        }).filter((row: FormatoHipItem) => row.idFormatoHip > 0);
      },
      error: (err: HttpErrorResponse) => {
        this.formatosHip = [];
        this.httpMsgHandler.handleMsg(err);
      },
      complete: () => {
        this.loadingFormatosHip = false;
      }
    });
  }

  private loadFormatoHipActivo(): void {
    if (!this.doctorId) {
      this.clearFormatoHipState();
      return;
    }

    this.doctorService.getFormatoHipByMedico(this.doctorId).subscribe({
      next: (res: DefaultResponse<any>) => {
        const idFormatoHip = Number(res?.data?.idFormatoHip ?? 1);
        this.formatoHipActivoId = idFormatoHip;
        this.formatoHipSeleccionadoId = idFormatoHip;
      },
      error: () => {
        this.formatoHipActivoId = 1;
        this.formatoHipSeleccionadoId = 1;
      }
    });
  }

  private clearFormatoHipState(): void {
    this.formatosHip = [];
    this.formatoHipActivoId = null;
    this.formatoHipSeleccionadoId = null;
    this.formatoHipPreviewActual = null;
    this.loadingFormatosHip = false;
    this.savingFormatoHip = false;
  }

  validateOldPin(): void {
    if (!this.pinExists) {
      // si no existe PIN, no hay nada que validar
      this.oldPinValidated = true;
      return;
    }

    const idUsuario = this.user?.idUsuario ? String(this.user.idUsuario) : null;
    const idPaciente = this.idPaciente ? String(this.idPaciente) : null;
    const oldPin = this.getOtpValue('old').trim();

    if (!idUsuario || !idPaciente) return;

    // valida formato 6 dígitos antes de pegarle al API
    this.pinForm.get('oldPin')?.markAsTouched();
    if (!this.isOtpComplete('old')) {
      Swal.fire({
        icon: 'warning',
        title: 'PIN ACTUAL INVÁLIDO',
        text: 'ESCRIBE 6 DÍGITOS.',
        confirmButtonText: 'ACEPTAR'
      }).then();
      return;
    }

    this.validatingOldPin = true;

    this.patientService.validatePinPaciente({ pin: oldPin }).subscribe({
      next: (res: any) => {
        const ok =
          res === true ||
          res?.data === true ||
          res?.success === true ||
          res?.isValid === true ||
          res?.data?.isValid === true;

        if (!ok) {
          this.validatingOldPin = false;
          this.oldPinValidated = false;

          Swal.fire({
            icon: 'error',
            title: 'PIN ACTUAL INCORRECTO',
            text: 'EL PIN ACTUAL NO COINCIDE. INTENTA DE NUEVO.',
            confirmButtonText: 'ACEPTAR'
          }).then(() => {
            this.resetOtp('old');
            setTimeout(() => this.focusOtpIndex('old', 0), 0);
          });

          return;
        }

        // ✅ OK: habilita etapa 2
        this.validatingOldPin = false;
        this.oldPinValidated = true;

        // limpia nuevo/confirm para capturarlos desde cero
        this.resetOtp('new');
        this.resetOtp('confirm');

        setTimeout(() => this.focusOtpIndex('new', 0), 0);
      },
      error: (err: any) => {
        this.validatingOldPin = false;
        this.httpMsgHandler.handleMsg(err);
      },
      complete: () => (this.validatingOldPin = false)
    });
  }

  // =========================
  // GUARDAR PIN PACIENTE
  // =========================
  savePacientePin(): void {
    const isPaciente =
      this.user?.tipoUsuario === 'Paciente' ||
      this.user?.idTipoUsuario === UserRole.Paciente;

    if (!isPaciente) return;

    // Si existe PIN, oldPin requerido
    const oldCtrl = this.pinForm.get('oldPin');
    if (this.pinExists) {
      oldCtrl?.enable({ emitEvent: false });
      oldCtrl?.setValidators([pin6DigitsValidator]);
    } else {
      oldCtrl?.clearValidators();
      oldCtrl?.setValue('', { emitEvent: false });
      oldCtrl?.disable({ emitEvent: false });
      this.resetOtp('old');
    }
    oldCtrl?.updateValueAndValidity({ emitEvent: false });

    this.pinForm.markAllAsTouched();
    this.pinForm.updateValueAndValidity({ emitEvent: false });

    if (this.pinExists && !this.oldPinValidated) {
      Swal.fire({
        icon: 'warning',
        title: 'VALIDA TU PIN ACTUAL',
        text: 'ANTES DE CAMBIARLO, PRIMERO VALIDA EL PIN ACTUAL.',
        confirmButtonText: 'ACEPTAR'
      }).then();
      return;
    }

    if (this.pinForm.invalid) {
      Swal.fire({
        icon: 'warning',
        title: 'REVISA EL PIN',
        text: this.pinExists
          ? 'ESCRIBE TU PIN ACTUAL, EL PIN NUEVO Y CONFÍRMALO.'
          : 'ESCRIBE TU PIN NUEVO Y CONFÍRMALO.',
        confirmButtonText: 'ACEPTAR'
      }).then();
      return;
    }

    const idUsuario = this.user?.idUsuario ? String(this.user.idUsuario) : null;
    const idPaciente = this.idPaciente ? String(this.idPaciente) : null;

    if (!idUsuario || !idPaciente) {
      Swal.fire({
        icon: 'error',
        title: 'NO SE PUDO GUARDAR',
        text: 'FALTAN DATOS DEL PACIENTE (IDUSUARIO/IDPACIENTE).',
        confirmButtonText: 'ACEPTAR'
      }).then();
      return;
    }

    const oldPin = this.getOtpValue('old').trim();
    const newPin = String(this.pinForm.get('newPin')?.value ?? '').trim();

    const guardarNuevoPin = () => {
      this.patientService.setPinPaciente({ pin: newPin }).subscribe({
        next: (res: any) => {
          this.httpMsgHandler.handleMsg(res);

          this.resetOtp('old');
          this.resetOtp('new');
          this.resetOtp('confirm');

          Swal.fire({
            icon: 'success',
            title: 'PIN ACTUALIZADO',
            text: 'EL PIN SE GUARDÓ CORRECTAMENTE.',
            confirmButtonText: 'ACEPTAR'
          }).then();
        },
        error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
      });
    };

    // Si NO existe pin -> guardar directo
    if (!this.pinExists) {
      guardarNuevoPin();
      return;
    }

    // Si ya existe -> validar PIN ACTUAL primero
    this.patientService.validatePinPaciente({ pin: oldPin }).subscribe({
      next: (res: any) => {
        const ok =
          res === true ||
          res?.data === true ||
          res?.success === true ||
          res?.isValid === true ||
          res?.data?.isValid === true;

        if (!ok) {
          Swal.fire({
            icon: 'error',
            title: 'PIN ACTUAL INCORRECTO',
            text: 'EL PIN ACTUAL NO COINCIDE. VERIFÍCALO E INTENTA DE NUEVO.',
            confirmButtonText: 'ACEPTAR'
          }).then();

          this.resetOtp('old');
          setTimeout(() => this.focusOtpIndex('old', 0), 0);
          return;
        }

        guardarNuevoPin();
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
    });
  }

  forgotPin(): void {
    // solo aplica si ya hay PIN
    if (!this.pinExists) return;

    Swal.fire({
      icon: 'question',
      title: 'RECUPERAR PIN',
      text: 'SE ENVIARÁN INSTRUCCIONES A TU CORREO PARA RECUPERAR O RESTABLECER TU PIN.',
      showCancelButton: true,
      confirmButtonText: 'ENVIAR',
      cancelButtonText: 'CANCELAR'
    }).then((r) => {
      if (!r.isConfirmed) return;

      this.validatingOldPin = true;

      this.patientService.forgotPinPaciente().subscribe({
        next: (res: any) => {
          // si el backend ya manda notificación, la mostramos
          this.httpMsgHandler.handleMsg(res);

          // por UX, limpiamos el OTP actual para que no se quede “medio escrito”
          this.resetOtp('old');
          this.oldPinValidated = false;

          Swal.fire({
            icon: 'success',
            title: 'LISTO',
            text: 'SI EL CORREO ESTÁ REGISTRADO, TE LLEGARÁN INSTRUCCIONES EN BREVE.',
            confirmButtonText: 'ACEPTAR'
          }).then(() => {
            setTimeout(() => this.focusOtpIndex('old', 0), 0);
          });
        },
        error: (err: any) => this.httpMsgHandler.handleMsg(err),
        complete: () => (this.validatingOldPin = false)
      });
    });
  }

  private loadTutoradosIfAny(): void {
    const idUsuarioTutor = this.user?.idUsuario ? String(this.user.idUsuario) : '';
    if (!idUsuarioTutor) {
      this.tutorados = [];
      this.isTutor = false;
      return;
    }

    this.tutorService.getPacientesByTutor(idUsuarioTutor).subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res ?? [];
        const list = Array.isArray(payload) ? payload : [];

        this.tutorados = list;
        this.isTutor = list.length > 0;

        // cargar imágenes
        this.tutorados.forEach(row => {
          const idUsuario = String(row?.idUsuario ?? row?.IdUsuario ?? '').trim();
          if (idUsuario) this.loadTutoradoImage(idUsuario);
        });
      },
      error: () => {
        this.tutorados = [];
        this.isTutor = false;
      }
    });
  }

  private loadTutoradoImage(idUsuario: string): void {
    if (!idUsuario) return;

    const cached = this.imageCache.get(idUsuario);
    if (cached !== undefined) {
      this.tutoradoImages[idUsuario] = cached;
      return;
    }

    this.userService.getUserImageBase64(idUsuario).subscribe({
      next: (img: string) => {
        const normalized = (img ?? '').trim();
        const value = normalized ? normalized : '';
        this.imageCache.set(idUsuario, value);
        this.tutoradoImages[idUsuario] = value;
      },
      error: () => {
        this.imageCache.set(idUsuario, '');
        this.tutoradoImages[idUsuario] = '';
      }
    });
  }

  getTutoradoNombre(row: any): string {
    const n = row?.nombres ?? row?.nombre ?? row?.Nombre ?? '';
    const a1 = row?.primerApellido ?? row?.apellidoPaterno ?? row?.PrimerApellido ?? '';
    const a2 = row?.segundoApellido ?? row?.apellidoMaterno ?? row?.SegundoApellido ?? '';
    return `${n} ${a1} ${a2}`.replace(/\s+/g, ' ').trim();
  }

  getTutoradoIdUsuario(row: any): string {
    return String(row?.idUsuario ?? row?.IdUsuario ?? '').trim();
  }

  getTutoradoImageSrc(row: any): string {
    const idUsuario = this.getTutoradoIdUsuario(row);
    const img = idUsuario ? (this.tutoradoImages[idUsuario] ?? '') : '';
    return img || 'assets/images/paciente.png';
  }

  openTutoradoProfile(row: any): void {
    const idUsuario = this.getTutoradoIdUsuario(row);
    if (!idUsuario) return;

    this.dialog.open(TutoradoProfileDialogComponent, {
      width: '920px',
      maxWidth: '96vw',
      autoFocus: false,
      data: { idUsuario }
    });
  }
}
